import React, { useState, useEffect, useCallback } from 'react';
import { auditAPI, clientAPI, userAPI } from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  Shield, Plus, Search, Eye, Edit2, Trash2, X,
  BarChart3, AlertTriangle, Clock, FileText,
  ChevronRight, Sparkles, Send, ThumbsUp, ThumbsDown,
  MessageSquare, RefreshCw, ClipboardCheck,
  TrendingUp, Calendar, ChevronLeft, BookOpen,
  ListChecks, Target, Download
} from 'lucide-react';
import { RISK_COLORS, STATUS_COLORS, OBS_STATUS_COLORS } from './audit/constants';
import AuditPlanning from './audit/AuditPlanning';
import AuditChecklists from './audit/AuditChecklists';
import AuditFollowUps from './audit/AuditFollowUps';
import AuditReport from './audit/AuditReport';

const AuditModule = () => {
  const { isPartner, isManager } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [observations, setObservations] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [constants, setConstants] = useState({});

  const [showEngagementForm, setShowEngagementForm] = useState(false);
  const [editingEngagement, setEditingEngagement] = useState(null);
  const [selectedEngagement, setSelectedEngagement] = useState(null);
  const [showObservationForm, setShowObservationForm] = useState(false);
  const [selectedObservation, setSelectedObservation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [engDetailTab, setEngDetailTab] = useState('observations');

  const defaultEngForm = {
    client_id: '', audit_type: 'Internal Audit', period_from: '', period_to: '',
    financial_year: '', branch_department: '', frequency: 'Quarterly',
    engagement_partner: '', audit_manager: '', team_members: [], risk_category: 'Medium', remarks: '',
  };
  const [engForm, setEngForm] = useState(defaultEngForm);

  const defaultObsForm = {
    audit_id: '', audit_area: 'Finance & Accounts', department: '',
    observation_date: new Date().toISOString().split('T')[0],
    hint: '', amount_involved: 0, risk_level: 'Medium',
    evidence_reference: '', recommendation_hint: '', priority: 'Medium',
  };
  const [obsForm, setObsForm] = useState(defaultObsForm);

  // Carry-forward state
  const [showCarryForward, setShowCarryForward] = useState(false);
  const [carryForwardSource, setCarryForwardSource] = useState('');
  const [carryForwardMode, setCarryForwardMode] = useState('all');

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [constRes, clientRes, userRes] = await Promise.all([
        auditAPI.getConstants(), clientAPI.getAll(), userAPI.getAll(),
      ]);
      setConstants(constRes.data);
      setClients(clientRes.data);
      setUsers(userRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadDashboard = useCallback(async () => {
    try { setStats((await auditAPI.getDashboardStats()).data); } catch (e) { console.error(e); }
  }, []);

  const loadEngagements = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      setEngagements((await auditAPI.getEngagements(params)).data);
    } catch (e) { console.error(e); }
  }, [filterStatus]);

  const loadObservations = useCallback(async (auditId) => {
    try {
      const params = auditId ? { audit_id: auditId } : {};
      setObservations((await auditAPI.getObservations(params)).data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadInitialData(); }, [loadInitialData]);
  useEffect(() => {
    if (activeTab === 'dashboard') loadDashboard();
    if (activeTab === 'engagements') loadEngagements();
  }, [activeTab, loadDashboard, loadEngagements]);

  useEffect(() => {
    if (selectedEngagement) loadObservations(selectedEngagement.id);
  }, [selectedEngagement, loadObservations]);

  // Reload engagement detail
  const reloadEngagement = async () => {
    if (!selectedEngagement) return;
    try {
      const res = await auditAPI.getEngagement(selectedEngagement.id);
      setSelectedEngagement(res.data);
    } catch (e) { console.error(e); }
  };

  const handleCreateEngagement = async () => {
    try {
      if (editingEngagement) {
        await auditAPI.updateEngagement(editingEngagement.id, engForm);
      } else {
        const res = await auditAPI.createEngagement(engForm);
        // Offer carry-forward for new engagement
        if (!editingEngagement && engagements.length > 0) {
          setSelectedEngagement(res.data);
          setShowCarryForward(true);
        }
      }
      setShowEngagementForm(false);
      setEditingEngagement(null);
      setEngForm(defaultEngForm);
      loadEngagements();
      loadDashboard();
    } catch (e) { console.error(e); alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleEditEngagement = (eng) => {
    setEngForm({
      client_id: eng.client_id, audit_type: eng.audit_type,
      period_from: eng.period_from?.split('T')[0] || '', period_to: eng.period_to?.split('T')[0] || '',
      financial_year: eng.financial_year, branch_department: eng.branch_department || '',
      frequency: eng.frequency, engagement_partner: eng.engagement_partner || '',
      audit_manager: eng.audit_manager || '', team_members: eng.team_members || [],
      risk_category: eng.risk_category, remarks: eng.remarks || '',
    });
    setEditingEngagement(eng);
    setShowEngagementForm(true);
  };

  const handleDeleteEngagement = async (id) => {
    if (!window.confirm('Delete this engagement and all related data?')) return;
    try {
      await auditAPI.deleteEngagement(id);
      loadEngagements(); loadDashboard();
    } catch (e) { console.error(e); }
  };

  const handleUpdateStatus = async (engId, newStatus) => {
    try {
      await auditAPI.updateEngagement(engId, { status: newStatus });
      loadEngagements(); loadDashboard();
    } catch (e) { console.error(e); }
  };

  const handleCreateObservation = async () => {
    try {
      await auditAPI.createObservation(obsForm);
      setShowObservationForm(false);
      setObsForm({ ...defaultObsForm, audit_id: selectedEngagement?.id || '' });
      loadObservations(selectedEngagement?.id);
      loadDashboard();
    } catch (e) { console.error(e); alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleGenerateAI = async (obsId) => {
    try {
      setLoading(true);
      await auditAPI.generateObservation(obsId);
      loadObservations(selectedEngagement?.id);
      setSelectedObservation((await auditAPI.getObservation(obsId)).data);
    } catch (e) { console.error(e); alert(e.response?.data?.detail || 'AI generation failed'); }
    setLoading(false);
  };

  const handleSendForReview = async (obsId) => {
    try { await auditAPI.sendForReview(obsId); loadObservations(selectedEngagement?.id); } catch (e) { console.error(e); }
  };

  const handleApprove = async (obsId) => {
    try {
      await auditAPI.approveObservation(obsId);
      loadObservations(selectedEngagement?.id);
      if (selectedObservation?.id === obsId) setSelectedObservation((await auditAPI.getObservation(obsId)).data);
    } catch (e) { console.error(e); }
  };

  const handleReject = async (obsId) => {
    try { await auditAPI.rejectObservation(obsId, 'Needs revision'); loadObservations(selectedEngagement?.id); } catch (e) { console.error(e); }
  };

  const handleManagementResponse = async (obsId, data) => {
    try {
      await auditAPI.addManagementResponse(obsId, data);
      loadObservations(selectedEngagement?.id);
      setSelectedObservation((await auditAPI.getObservation(obsId)).data);
    } catch (e) { console.error(e); alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleRejoinder = async (obsId, data) => {
    try {
      await auditAPI.addRejoinder(obsId, data);
      loadObservations(selectedEngagement?.id);
      setSelectedObservation((await auditAPI.getObservation(obsId)).data);
    } catch (e) { console.error(e); alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleCarryForward = async () => {
    if (!carryForwardSource || !selectedEngagement) return;
    try {
      const res = await auditAPI.importObservations(selectedEngagement.id, carryForwardSource, carryForwardMode);
      alert(res.data.message);
      setShowCarryForward(false);
      loadObservations(selectedEngagement.id);
    } catch (e) { console.error(e); alert(e.response?.data?.detail || 'Failed'); }
  };

  const filteredEngagements = engagements.filter(e =>
    !searchTerm || e.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.audit_type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.financial_year?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'engagements', label: 'Engagements', icon: FileText },
  ];

  // ==================== RENDER ====================
  return (
    <div className="space-y-4" data-testid="audit-module">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="audit-module-title">
            <Shield className="w-6 h-6 text-indigo-600" /> Internal Audit
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage audit engagements, observations & reports</p>
        </div>
      </div>

      {/* Top-level Tabs (only shown when no engagement selected) */}
      {!selectedEngagement && (
        <div className="border-b border-gray-200">
          <nav className="flex space-x-1 overflow-x-auto" data-testid="audit-tabs">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`audit-tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Dashboard */}
      {!selectedEngagement && activeTab === 'dashboard' && <DashboardTab stats={stats} />}

      {/* Engagements List */}
      {!selectedEngagement && activeTab === 'engagements' && (
        <EngagementsTab
          engagements={filteredEngagements} searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          filterStatus={filterStatus} setFilterStatus={setFilterStatus} constants={constants}
          showForm={showEngagementForm} setShowForm={setShowEngagementForm}
          form={engForm} setForm={setEngForm} defaultForm={defaultEngForm}
          editing={editingEngagement} setEditing={setEditingEngagement} clients={clients} users={users}
          onCreate={handleCreateEngagement} onEdit={handleEditEngagement} onDelete={handleDeleteEngagement}
          onUpdateStatus={handleUpdateStatus}
          onViewDetail={(eng) => { setSelectedEngagement(eng); setEngDetailTab('observations'); }}
          isPartner={isPartner} isManager={isManager}
        />
      )}

      {/* Engagement Detail View */}
      {selectedEngagement && (
        <EngagementDetailView
          engagement={selectedEngagement}
          observations={observations}
          constants={constants}
          engDetailTab={engDetailTab}
          setEngDetailTab={setEngDetailTab}
          showObservationForm={showObservationForm}
          setShowObservationForm={setShowObservationForm}
          obsForm={obsForm} setObsForm={setObsForm} defaultObsForm={defaultObsForm}
          selectedObs={selectedObservation} setSelectedObs={setSelectedObservation}
          onCreateObs={handleCreateObservation}
          onGenerate={handleGenerateAI} onSendForReview={handleSendForReview}
          onApprove={handleApprove} onReject={handleReject}
          onManagementResponse={handleManagementResponse}
          onRejoinder={handleRejoinder}
          loading={loading}
          onBack={() => { setSelectedEngagement(null); setActiveTab('engagements'); }}
          onReloadEngagement={reloadEngagement}
          isPartner={isPartner} isManager={isManager}
          showCarryForward={showCarryForward} setShowCarryForward={setShowCarryForward}
          carryForwardSource={carryForwardSource} setCarryForwardSource={setCarryForwardSource}
          carryForwardMode={carryForwardMode} setCarryForwardMode={setCarryForwardMode}
          onCarryForward={handleCarryForward} engagements={engagements}
        />
      )}
    </div>
  );
};

// ==================== DASHBOARD ====================
const DashboardTab = ({ stats }) => {
  if (!stats) return <div className="text-center py-12 text-gray-500">Loading dashboard...</div>;
  const statCards = [
    { label: 'Total Audits', value: stats.total_audits, icon: FileText, color: 'bg-indigo-50 text-indigo-700', iconBg: 'bg-indigo-100' },
    { label: 'Active Audits', value: stats.active_audits, icon: TrendingUp, color: 'bg-blue-50 text-blue-700', iconBg: 'bg-blue-100' },
    { label: 'Total Observations', value: stats.total_observations, icon: AlertTriangle, color: 'bg-amber-50 text-amber-700', iconBg: 'bg-amber-100' },
    { label: 'Pending Approvals', value: stats.pending_approvals, icon: Clock, color: 'bg-purple-50 text-purple-700', iconBg: 'bg-purple-100' },
    { label: 'Pending Replies', value: stats.pending_replies, icon: MessageSquare, color: 'bg-orange-50 text-orange-700', iconBg: 'bg-orange-100' },
    { label: 'Open Observations', value: stats.open_observations, icon: AlertTriangle, color: 'bg-red-50 text-red-700', iconBg: 'bg-red-100' },
    { label: 'Overdue Closures', value: stats.overdue_closures, icon: Clock, color: 'bg-rose-50 text-rose-700', iconBg: 'bg-rose-100' },
  ];
  return (
    <div className="space-y-6" data-testid="audit-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {statCards.map((c, i) => (
          <div key={i} className={`rounded-xl p-4 ${c.color}`} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${c.iconBg}`}><c.icon className="w-5 h-5" /></div>
              <div><p className="text-2xl font-bold">{c.value}</p><p className="text-xs font-medium opacity-80">{c.label}</p></div>
            </div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Observations by Risk Level</h3>
          {Object.entries(stats.observations_by_risk || {}).map(([risk, count]) => {
            const total = Object.values(stats.observations_by_risk).reduce((a, b) => a + b, 0) || 1;
            const pct = Math.round((count / total) * 100);
            return (
              <div key={risk} className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${RISK_COLORS[risk] || 'bg-gray-100'}`}>{risk}</span>
                  <span className="font-medium">{count} ({pct}%)</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className={`h-2 rounded-full ${risk === 'Critical' ? 'bg-red-500' : risk === 'High' ? 'bg-orange-500' : risk === 'Medium' ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {!Object.keys(stats.observations_by_risk || {}).length && <p className="text-gray-400 text-sm text-center py-4">No observations yet</p>}
        </div>
        <div className="bg-white rounded-xl border p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Engagements by Status</h3>
          {Object.entries(stats.engagements_by_status || {}).map(([status, count]) => (
            <div key={status} className="flex items-center justify-between mb-2">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100'}`}>{status}</span>
              <span className="text-sm font-bold text-gray-700">{count}</span>
            </div>
          ))}
          {!Object.keys(stats.engagements_by_status || {}).length && <p className="text-gray-400 text-sm text-center py-4">No engagements yet</p>}
        </div>
      </div>
    </div>
  );
};

// ==================== ENGAGEMENTS LIST ====================
const EngagementsTab = ({
  engagements, searchTerm, setSearchTerm, filterStatus, setFilterStatus,
  constants, showForm, setShowForm, form, setForm, defaultForm,
  editing, setEditing, clients, users, onCreate, onEdit, onDelete,
  onUpdateStatus, onViewDetail, isPartner, isManager
}) => {
  const statusList = constants.audit_statuses || [];
  return (
    <div className="space-y-4" data-testid="engagements-tab">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search engagements..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="search-engagements" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-status">
          <option value="">All Statuses</option>
          {statusList.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(isPartner || isManager) && (
          <button onClick={() => { setForm(defaultForm); setEditing(null); setShowForm(true); }} className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 whitespace-nowrap" data-testid="create-engagement-btn">
            <Plus className="w-4 h-4" /> New Engagement
          </button>
        )}
      </div>
      {showForm && <EngagementFormModal form={form} setForm={setForm} editing={editing} clients={clients} users={users} constants={constants} onSubmit={onCreate} onClose={() => { setShowForm(false); setEditing(null); }} />}
      {engagements.length === 0 ? (
        <div className="text-center py-16 text-gray-400"><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p className="font-medium">No audit engagements yet</p></div>
      ) : (
        <div className="grid gap-3">
          {engagements.map(eng => (
            <div key={eng.id} className="bg-white rounded-xl border hover:shadow-md transition-shadow p-4 cursor-pointer" onClick={() => onViewDetail(eng)} data-testid={`engagement-card-${eng.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 truncate">{eng.client_name}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[eng.status] || 'bg-gray-100'}`}>{eng.status}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RISK_COLORS[eng.risk_category] || ''}`}>{eng.risk_category}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500 flex-wrap">
                    <span className="flex items-center gap-1"><Shield className="w-3 h-3" />{eng.audit_type}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{eng.period_from?.split('T')[0]} to {eng.period_to?.split('T')[0]}</span>
                    <span>FY: {eng.financial_year}</span>
                    {eng.observation_count > 0 && <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{eng.observation_count} obs</span>}
                  </div>
                  {eng.engagement_partner_name && <p className="text-xs text-gray-400 mt-1">Partner: {eng.engagement_partner_name}{eng.audit_manager_name ? ` | Manager: ${eng.audit_manager_name}` : ''}</p>}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                  {(isPartner || isManager) && (
                    <>
                      <button onClick={() => onEdit(eng)} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg" title="Edit" data-testid={`edit-eng-${eng.id}`}><Edit2 className="w-4 h-4" /></button>
                      <select value={eng.status} onChange={e => onUpdateStatus(eng.id, e.target.value)} className="text-xs border rounded px-2 py-1.5" data-testid={`status-select-${eng.id}`}>
                        {statusList.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      {isPartner && <button onClick={() => onDelete(eng.id)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Delete" data-testid={`delete-eng-${eng.id}`}><Trash2 className="w-4 h-4" /></button>}
                    </>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== ENGAGEMENT FORM MODAL ====================
const EngagementFormModal = ({ form, setForm, editing, clients, users, constants, onSubmit, onClose }) => {
  const activeClients = clients.filter(c => c.is_active);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="engagement-form-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-lg font-semibold">{editing ? 'Edit Engagement' : 'New Audit Engagement'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
            <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="eng-client-select"><option value="">Select Client</option>{activeClients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Audit Type</label><select value={form.audit_type} onChange={e => setForm({ ...form, audit_type: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="eng-type-select">{(constants.audit_types || []).map(t => <option key={t} value={t}>{t}</option>)}</select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label><select value={form.frequency} onChange={e => setForm({ ...form, frequency: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm">{(constants.audit_frequencies || []).map(f => <option key={f} value={f}>{f}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Period From *</label><input type="date" value={form.period_from} onChange={e => setForm({ ...form, period_from: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="eng-period-from" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Period To *</label><input type="date" value={form.period_to} onChange={e => setForm({ ...form, period_to: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="eng-period-to" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Financial Year *</label><input type="text" placeholder="2025-26" value={form.financial_year} onChange={e => setForm({ ...form, financial_year: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="eng-fy" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Branch / Department</label><input type="text" value={form.branch_department} onChange={e => setForm({ ...form, branch_department: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Risk Category</label><select value={form.risk_category} onChange={e => setForm({ ...form, risk_category: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="eng-risk-select">{(constants.risk_categories || []).map(r => <option key={r} value={r}>{r}</option>)}</select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Engagement Partner</label><select value={form.engagement_partner} onChange={e => setForm({ ...form, engagement_partner: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm"><option value="">Select</option>{users.filter(u => u.role === 'partner').map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}</select></div>
            <div><label className="block text-sm font-medium text-gray-700 mb-1">Audit Manager</label><select value={form.audit_manager} onChange={e => setForm({ ...form, audit_manager: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm"><option value="">Select</option>{users.filter(u => ['partner', 'manager'].includes(u.role)).map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}</select></div>
          </div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label><textarea value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={onSubmit} disabled={!form.client_id || !form.period_from || !form.period_to || !form.financial_year} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50" data-testid="save-engagement-btn">{editing ? 'Update' : 'Create'} Engagement</button>
        </div>
      </div>
    </div>
  );
};

// ==================== ENGAGEMENT DETAIL VIEW ====================
const EngagementDetailView = ({
  engagement, observations, constants, engDetailTab, setEngDetailTab,
  showObservationForm, setShowObservationForm, obsForm, setObsForm, defaultObsForm,
  selectedObs, setSelectedObs, onCreateObs, onGenerate, onSendForReview,
  onApprove, onReject, onManagementResponse, onRejoinder, loading,
  onBack, onReloadEngagement, isPartner, isManager,
  showCarryForward, setShowCarryForward, carryForwardSource, setCarryForwardSource,
  carryForwardMode, setCarryForwardMode, onCarryForward, engagements
}) => {
  const detailTabs = [
    { id: 'observations', label: 'Observations', icon: AlertTriangle },
    { id: 'planning', label: 'Planning', icon: BookOpen },
    { id: 'checklists', label: 'Checklists', icon: ListChecks },
    { id: 'followups', label: 'Follow-Ups', icon: Target },
    { id: 'report', label: 'Report', icon: FileText },
  ];

  return (
    <div className="space-y-4" data-testid="engagement-detail">
      {/* Back + Engagement Info Header */}
      <div className="bg-white rounded-xl border p-4">
        <div className="flex items-start gap-3">
          <button onClick={onBack} className="p-1.5 hover:bg-gray-100 rounded-lg mt-0.5" data-testid="back-to-engagements"><ChevronLeft className="w-5 h-5" /></button>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-gray-900">{engagement.client_name}</h2>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[engagement.status] || 'bg-gray-100'}`}>{engagement.status}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RISK_COLORS[engagement.risk_category] || ''}`}>{engagement.risk_category}</span>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
              <span>{engagement.audit_type}</span>
              <span>{engagement.period_from?.split('T')[0]} to {engagement.period_to?.split('T')[0]}</span>
              <span>FY: {engagement.financial_year}</span>
              {engagement.engagement_partner_name && <span>Partner: {engagement.engagement_partner_name}</span>}
              {engagement.audit_manager_name && <span>Manager: {engagement.audit_manager_name}</span>}
            </div>
          </div>
          {(isPartner || isManager) && (
            <button onClick={() => setShowCarryForward(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100" data-testid="carry-forward-btn" title="Import observations from previous audit">
              <Download className="w-3.5 h-3.5" /> Import
            </button>
          )}
        </div>
      </div>

      {/* Carry-Forward Modal */}
      {showCarryForward && (
        <CarryForwardModal
          engagements={engagements} currentId={engagement.id}
          source={carryForwardSource} setSource={setCarryForwardSource}
          mode={carryForwardMode} setMode={setCarryForwardMode}
          onImport={onCarryForward} onClose={() => setShowCarryForward(false)}
        />
      )}

      {/* Sub-Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-1 overflow-x-auto" data-testid="engagement-detail-tabs">
          {detailTabs.map(tab => (
            <button key={tab.id} onClick={() => setEngDetailTab(tab.id)} data-testid={`detail-tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${engDetailTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
              {tab.id === 'observations' && observations.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">{observations.length}</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* Sub-Tab Content */}
      {engDetailTab === 'observations' && (
        <ObservationsContent
          observations={observations} engagement={engagement} constants={constants}
          showForm={showObservationForm} setShowForm={setShowObservationForm}
          form={obsForm} setForm={setObsForm} defaultForm={defaultObsForm}
          selectedObs={selectedObs} setSelectedObs={setSelectedObs}
          onCreate={onCreateObs} onGenerate={onGenerate} onSendForReview={onSendForReview}
          onApprove={onApprove} onReject={onReject}
          onManagementResponse={onManagementResponse} onRejoinder={onRejoinder}
          loading={loading} isPartner={isPartner} isManager={isManager}
        />
      )}
      {engDetailTab === 'planning' && <AuditPlanning engagement={engagement} onUpdate={onReloadEngagement} />}
      {engDetailTab === 'checklists' && <AuditChecklists engagement={engagement} />}
      {engDetailTab === 'followups' && <AuditFollowUps engagement={engagement} observations={observations} />}
      {engDetailTab === 'report' && <AuditReport engagement={engagement} />}
    </div>
  );
};

// ==================== CARRY-FORWARD MODAL ====================
const CarryForwardModal = ({ engagements, currentId, source, setSource, mode, setMode, onImport, onClose }) => {
  const otherEngagements = engagements.filter(e => e.id !== currentId);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="carry-forward-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-semibold">Import Pending Observations</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">Import unresolved observations from a previous audit period.</p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Source Audit</label>
            <select value={source} onChange={e => setSource(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="cf-source-select">
              <option value="">Select previous audit...</option>
              {otherEngagements.map(e => <option key={e.id} value={e.id}>{e.client_name} - {e.audit_type} ({e.period_from?.split('T')[0]} to {e.period_to?.split('T')[0]})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Import Mode</label>
            <div className="space-y-2">
              {[{ v: 'all', l: 'Import All Pending' }, { v: 'high_risk', l: 'High Risk Only' }].map(opt => (
                <label key={opt.v} className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="cfmode" value={opt.v} checked={mode === opt.v} onChange={e => setMode(e.target.value)} className="text-indigo-600" />
                  <span className="text-sm">{opt.l}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={onImport} disabled={!source} className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50" data-testid="cf-import-btn">Import Observations</button>
        </div>
      </div>
    </div>
  );
};

// ==================== OBSERVATIONS CONTENT ====================
const ObservationsContent = ({
  observations, engagement, constants, showForm, setShowForm,
  form, setForm, defaultForm, selectedObs, setSelectedObs,
  onCreate, onGenerate, onSendForReview, onApprove, onReject,
  onManagementResponse, onRejoinder, loading, isPartner, isManager
}) => (
  <div className="space-y-4" data-testid="observations-content">
    <div className="flex items-center justify-end">
      {(isPartner || isManager) && (
        <button onClick={() => { setForm({ ...defaultForm, audit_id: engagement.id }); setShowForm(true); }} className="flex items-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700" data-testid="add-observation-btn">
          <Plus className="w-4 h-4" /> Add Observation
        </button>
      )}
    </div>
    {showForm && <ObservationFormModal form={form} setForm={setForm} constants={constants} onSubmit={onCreate} onClose={() => setShowForm(false)} />}
    {selectedObs && (
      <ObservationDetailModal obs={selectedObs} onClose={() => setSelectedObs(null)}
        onGenerate={onGenerate} onSendForReview={onSendForReview} onApprove={onApprove} onReject={onReject}
        onManagementResponse={onManagementResponse} onRejoinder={onRejoinder}
        loading={loading} isPartner={isPartner} isManager={isManager} />
    )}
    {observations.length === 0 && !showForm && (
      <div className="text-center py-12 text-gray-400"><ClipboardCheck className="w-10 h-10 mx-auto mb-3 opacity-50" /><p className="font-medium">No observations yet</p></div>
    )}
    <div className="space-y-2">
      {observations.map(obs => (
        <div key={obs.id} className="bg-white rounded-xl border hover:shadow-sm transition-shadow p-4 cursor-pointer" onClick={() => setSelectedObs(obs)} data-testid={`observation-card-${obs.id}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${OBS_STATUS_COLORS[obs.status] || 'bg-gray-100'}`}>{obs.status}</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${RISK_COLORS[obs.risk_level] || ''}`}>{obs.risk_level}</span>
                <span className="text-xs text-gray-400">{obs.audit_area}</span>
                {obs.carry_forward?.is_carried_forward && <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 flex items-center gap-1"><RefreshCw className="w-3 h-3" />CF#{obs.carry_forward.carry_forward_count}</span>}
              </div>
              <p className="text-sm font-medium text-gray-800">{obs.generated?.title || obs.hint?.substring(0, 80) || 'Untitled'}</p>
              <p className="text-xs text-gray-500 mt-1 truncate">{obs.generated?.short_summary || obs.hint?.substring(0, 120)}</p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
              {obs.status === 'Draft' && <button onClick={() => onGenerate(obs.id)} className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg" title="Generate AI Draft" data-testid={`generate-btn-${obs.id}`}><Sparkles className="w-4 h-4" /></button>}
              {obs.status === 'Generated' && <button onClick={() => onSendForReview(obs.id)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Send for Review"><Send className="w-4 h-4" /></button>}
              {['Generated', 'Sent for Review'].includes(obs.status) && (isPartner || isManager) && (
                <>
                  <button onClick={() => onApprove(obs.id)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg" title="Approve" data-testid={`approve-btn-${obs.id}`}><ThumbsUp className="w-4 h-4" /></button>
                  <button onClick={() => onReject(obs.id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="Reject"><ThumbsDown className="w-4 h-4" /></button>
                </>
              )}
              <ChevronRight className="w-4 h-4 text-gray-300" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ==================== OBSERVATION FORM MODAL ====================
const ObservationFormModal = ({ form, setForm, constants, onSubmit, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="observation-form-modal" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-2xl z-10">
        <h2 className="text-lg font-semibold">New Observation (Staff Hint)</h2>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Audit Area</label><select value={form.audit_area} onChange={e => setForm({ ...form, audit_area: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="obs-area-select">{(constants.audit_areas || []).map(a => <option key={a} value={a}>{a}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Department</label><input type="text" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
        </div>
        <div><label className="block text-sm font-medium text-gray-700 mb-1">Staff Hint / Raw Notes *</label><textarea value={form.hint} onChange={e => setForm({ ...form, hint: e.target.value })} rows={3} placeholder="e.g., cash vouchers missing approval 6 cases amount 125000" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="obs-hint-input" /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Amount Involved</label><input type="number" value={form.amount_involved} onChange={e => setForm({ ...form, amount_involved: parseFloat(e.target.value) || 0 })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="obs-amount-input" /></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Risk Level</label><select value={form.risk_level} onChange={e => setForm({ ...form, risk_level: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="obs-risk-select">{(constants.risk_categories || []).map(r => <option key={r} value={r}>{r}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Priority</label><select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm"><option value="Low">Low</option><option value="Medium">Medium</option><option value="High">High</option></select></div>
        </div>
        <div><label className="block text-sm font-medium text-gray-700 mb-1">Recommendation Hint</label><input type="text" value={form.recommendation_hint} onChange={e => setForm({ ...form, recommendation_hint: e.target.value })} placeholder="e.g., all cash vouchers should be approved" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="obs-recommendation-input" /></div>
        <div><label className="block text-sm font-medium text-gray-700 mb-1">Evidence Reference</label><input type="text" value={form.evidence_reference} onChange={e => setForm({ ...form, evidence_reference: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" /></div>
      </div>
      <div className="flex justify-end gap-2 p-5 border-t">
        <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
        <button onClick={onSubmit} disabled={!form.hint} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50" data-testid="save-observation-btn">Save Observation</button>
      </div>
    </div>
  </div>
);

// ==================== OBSERVATION DETAIL MODAL (Enhanced with Response & Rejoinder) ====================
const ObservationDetailModal = ({ obs, onClose, onGenerate, onSendForReview, onApprove, onReject, onManagementResponse, onRejoinder, loading, isPartner, isManager }) => {
  const gen = obs.generated || {};
  const hasGenerated = gen.title || gen.observation;
  const [activeSection, setActiveSection] = useState('details');
  const [respForm, setRespForm] = useState({ response_text: '', responded_by: '', designation: '', corrective_action: '', target_closure_date: '' });
  const [rejForm, setRejForm] = useState({ comment: '', conclusion: '', final_status: 'Open' });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="observation-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b sticky top-0 bg-white rounded-t-2xl z-10">
          <div>
            <h2 className="text-lg font-semibold">{gen.title || 'Observation Detail'}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${OBS_STATUS_COLORS[obs.status] || 'bg-gray-100'}`}>{obs.status}</span>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${RISK_COLORS[obs.risk_level] || ''}`}>{obs.risk_level}</span>
              <span className="text-xs text-gray-400">{obs.audit_area}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b px-5 gap-1 overflow-x-auto">
          {[{ id: 'details', label: 'Details' }, { id: 'response', label: 'Mgmt Response' }, { id: 'rejoinder', label: 'Rejoinder' }, { id: 'history', label: 'History' }].map(t => (
            <button key={t.id} onClick={() => setActiveSection(t.id)} data-testid={`obs-section-${t.id}`}
              className={`px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap ${activeSection === t.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-5 space-y-4">
          {/* Details Section */}
          {activeSection === 'details' && (
            <>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Staff Hint / Raw Notes</h4>
                <p className="text-sm text-gray-800">{obs.hint}</p>
                {obs.recommendation_hint && <p className="text-xs text-gray-500 mt-1">Rec. hint: {obs.recommendation_hint}</p>}
                {obs.amount_involved > 0 && <p className="text-xs text-gray-500 mt-1">Amount: Rs. {obs.amount_involved?.toLocaleString('en-IN')}</p>}
              </div>
              {hasGenerated && (
                <div className="space-y-3">
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                    <h4 className="text-xs font-semibold text-indigo-600 uppercase mb-2 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Generated Observation</h4>
                    <p className="text-sm text-gray-800 leading-relaxed">{gen.observation}</p>
                  </div>
                  {gen.risk_impact && <div className="bg-orange-50 rounded-lg p-4 border border-orange-100"><h4 className="text-xs font-semibold text-orange-600 uppercase mb-2">Risk / Impact</h4><p className="text-sm text-gray-800">{gen.risk_impact}</p></div>}
                  {gen.recommendation && <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100"><h4 className="text-xs font-semibold text-emerald-600 uppercase mb-2">Recommendation</h4><p className="text-sm text-gray-800">{gen.recommendation}</p></div>}
                </div>
              )}
            </>
          )}

          {/* Management Response Section */}
          {activeSection === 'response' && (
            <div className="space-y-4">
              {obs.management_response?.response_text && (
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                  <h4 className="text-xs font-semibold text-blue-600 uppercase mb-2">Current Response ({obs.management_response.status})</h4>
                  <p className="text-sm text-gray-800">{obs.management_response.response_text}</p>
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    {obs.management_response.responded_by && <span>By: {obs.management_response.responded_by}</span>}
                    {obs.management_response.designation && <span>({obs.management_response.designation})</span>}
                    {obs.management_response.corrective_action && <span>Action: {obs.management_response.corrective_action}</span>}
                  </div>
                </div>
              )}
              <div className="bg-white rounded-lg border p-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-700">{obs.management_response?.response_text ? 'Update Response' : 'Add Management Response'}</h4>
                <textarea value={respForm.response_text} onChange={e => setRespForm({ ...respForm, response_text: e.target.value })} rows={3} placeholder="Management/client response text..." className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="mgmt-response-text" />
                <div className="grid grid-cols-2 gap-3">
                  <input type="text" value={respForm.responded_by} onChange={e => setRespForm({ ...respForm, responded_by: e.target.value })} placeholder="Responded by (name)" className="border rounded-lg px-3 py-2 text-sm" data-testid="mgmt-responded-by" />
                  <input type="text" value={respForm.designation} onChange={e => setRespForm({ ...respForm, designation: e.target.value })} placeholder="Designation" className="border rounded-lg px-3 py-2 text-sm" />
                </div>
                <textarea value={respForm.corrective_action} onChange={e => setRespForm({ ...respForm, corrective_action: e.target.value })} rows={2} placeholder="Corrective action proposed..." className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="mgmt-corrective-action" />
                <div><label className="block text-xs text-gray-500 mb-1">Target Closure Date</label><input type="date" value={respForm.target_closure_date} onChange={e => setRespForm({ ...respForm, target_closure_date: e.target.value })} className="border rounded-lg px-3 py-2 text-sm" data-testid="mgmt-target-date" /></div>
                <button
                  onClick={() => { if (respForm.response_text && respForm.responded_by) { onManagementResponse(obs.id, respForm); setRespForm({ response_text: '', responded_by: '', designation: '', corrective_action: '', target_closure_date: '' }); } else alert('Response text and Responded By are required'); }}
                  className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                  data-testid="submit-mgmt-response"
                >Submit Response</button>
              </div>
            </div>
          )}

          {/* Auditor Rejoinder Section */}
          {activeSection === 'rejoinder' && (
            <div className="space-y-4">
              {obs.auditor_rejoinder?.comment && (
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                  <h4 className="text-xs font-semibold text-purple-600 uppercase mb-2">Current Rejoinder</h4>
                  <p className="text-sm text-gray-800">{obs.auditor_rejoinder.comment}</p>
                  {obs.auditor_rejoinder.conclusion && <p className="text-xs text-gray-600 mt-1">Conclusion: {obs.auditor_rejoinder.conclusion}</p>}
                  <p className="text-xs text-gray-500 mt-1">Final Status: <span className="font-medium">{obs.auditor_rejoinder.final_status}</span></p>
                </div>
              )}
              {(isPartner || isManager) && (
                <div className="bg-white rounded-lg border p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-gray-700">{obs.auditor_rejoinder?.comment ? 'Update Rejoinder' : 'Add Auditor Rejoinder'}</h4>
                  <textarea value={rejForm.comment} onChange={e => setRejForm({ ...rejForm, comment: e.target.value })} rows={3} placeholder="Auditor's comment on management response..." className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="rejoinder-comment" />
                  <textarea value={rejForm.conclusion} onChange={e => setRejForm({ ...rejForm, conclusion: e.target.value })} rows={2} placeholder="Conclusion..." className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="rejoinder-conclusion" />
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Observation Final Status</label>
                    <select value={rejForm.final_status} onChange={e => setRejForm({ ...rejForm, final_status: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="rejoinder-status">
                      <option value="Open">Open</option>
                      <option value="Action Pending">Action Pending</option>
                      <option value="Evidence Pending">Evidence Pending</option>
                      <option value="Closed">Closed</option>
                      <option value="Carried Forward">Carried Forward</option>
                    </select>
                  </div>
                  <button
                    onClick={() => { if (rejForm.comment) { onRejoinder(obs.id, rejForm); setRejForm({ comment: '', conclusion: '', final_status: 'Open' }); } else alert('Comment is required'); }}
                    className="w-full py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
                    data-testid="submit-rejoinder"
                  >Submit Rejoinder</button>
                </div>
              )}
            </div>
          )}

          {/* Version History */}
          {activeSection === 'history' && obs.versions?.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-3">Version History</h4>
              <div className="space-y-1">
                {obs.versions.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-500 py-2 border-b border-gray-50">
                    <span className="font-medium">v{v.version_number}</span>
                    <span className="capitalize px-1.5 py-0.5 bg-gray-100 rounded">{v.type}</span>
                    <span>{v.created_by_name}</span>
                    {v.approved_by && <span className="text-emerald-600">Approved: {v.approved_by}</span>}
                    <span className="ml-auto">{v.created_at?.split('T')[0]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions Bar */}
        <div className="flex flex-wrap gap-2 p-5 border-t">
          {obs.status === 'Draft' && <button onClick={() => onGenerate(obs.id)} disabled={loading} className="flex items-center gap-1.5 bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50" data-testid="generate-draft-btn"><Sparkles className="w-4 h-4" /> {loading ? 'Generating...' : 'Generate Draft'}</button>}
          {obs.status === 'Generated' && <button onClick={() => onSendForReview(obs.id)} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700" data-testid="send-review-btn"><Send className="w-4 h-4" /> Send for Review</button>}
          {['Generated', 'Sent for Review'].includes(obs.status) && (isPartner || isManager) && (
            <>
              <button onClick={() => onApprove(obs.id)} className="flex items-center gap-1.5 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700" data-testid="approve-obs-btn"><ThumbsUp className="w-4 h-4" /> Approve</button>
              <button onClick={() => onReject(obs.id)} className="flex items-center gap-1.5 bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600" data-testid="reject-obs-btn"><ThumbsDown className="w-4 h-4" /> Reject</button>
            </>
          )}
          <button onClick={onClose} className="ml-auto px-4 py-2 text-sm border rounded-lg hover:bg-gray-50" data-testid="close-obs-detail">Close</button>
        </div>
      </div>
    </div>
  );
};

export default AuditModule;
